import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { infiniteScrollHook } from "../../hooks/infiniteScrollHook";
import TenantListingTopHeader from "../../newComponents/listings/top-listing-headers/tenant-listing-top-header";
import CurrentTenantCard from "../../newComponents/listings/listing-cards/current-tenant";
import ProspectTenantCard from "../../newComponents/listings/listing-cards/prospect-tenant";
import { Route, Routes, useLocation, useNavigate } from "react-router";
import AddTenant from "../../newComponents/tenant/AddTenant";
import EditTenant from "../../newComponents/tenant/EditTenant";
import { TenantStatusesEnum } from "../../components/common/Enum";
import { LeaseApplicationService, TenantService } from "@propertelligent/client-api";
import CurrentTenantDetails2 from "../../newComponents/tenant/CurrentTenantDetails";
import LandlordTenantOnboard from "../../components/tenants/landlordTenantOnboard";
import RenewalLease from "../../components/tenants/RenewalLease";
import MoveInAndOutContentBlocks from "../../components/tenants/common/MoveInAndOutContentBlocks";
import LeaseApplicationReviewView from "../../newComponents/tenant/ReviewApplication";
import LeaseAgreementReviewView from "../../newComponents/tenant/ReviewAgreement";
import { usePermissions } from "../../hooks/usePermissions";
import NoAccess from "../NoAccess";
import { PermissionGuard } from "../../newComponents/PermissionGuard";
import { AccessLevelEnum } from "@propertelligent/client-api";
import { PubSub, TopicsEnum } from "../../utils/pubsub";

// Define the props interface for the MainContent component
interface MainContentProps {
  className?: string;
  handleSort: () => void;
  currentSortDirection: string;
  rowCount: number;
  handleSelectChange: (value: string, label: string) => void;
  onSearchTermChange: (term: string) => void;
  filterOptions: { title: string; count: number }[];
  setActiveTab: (tab: string) => void;
  activeTab: string;
  aggregateValuesByTab: { Current: Record<string, number>; Prospect: Record<string, number>; };
  selectedFilterLabel: string;
  commonProps: any;
  detailsActiveTab?: string;
  hasAnyTenants: boolean;
  totalItemCount: number;
  showAddButton?: boolean;
}

const MainContent = ({
  className,
  handleSort,
  currentSortDirection,
  rowCount,
  handleSelectChange,
  onSearchTermChange,
  filterOptions,
  setActiveTab,
  activeTab,
  aggregateValuesByTab,
  selectedFilterLabel,
  commonProps,
  detailsActiveTab,
  hasAnyTenants,
  totalItemCount,
  showAddButton = true,
}: MainContentProps) => {
  const navigate = useNavigate();

  return (
    <div className="content-wrapper container-fluid">
      {hasAnyTenants && (
        <TenantListingTopHeader
          className={className}
          PageHeading="Tenants"
          ButtonName="Add Tenant"
          handleSort={handleSort}
          currentSortDirection={currentSortDirection}
          Count={rowCount}
          handleSelectChange={handleSelectChange}
          onSearchTermChange={onSearchTermChange}
          filterOptions={filterOptions}
          setActiveTab={setActiveTab}
          activeTab={activeTab}
          aggregateValuesFilters={aggregateValuesByTab[activeTab as keyof typeof aggregateValuesByTab]}
          selectedFilterLabel={selectedFilterLabel}
          totalItemCount={totalItemCount}
          showAddButton={showAddButton}
          onClick={() =>
            navigate("/tenants/create", {
              state: { from: window.location.pathname, activeTab: detailsActiveTab },
            })
          }
        />
      )}
      {activeTab === "Current" && <CurrentTenantCard Id="CurrentTenant" {...commonProps} hasAnyTenants={hasAnyTenants} className={className} />}
      {activeTab === "Prospect" && <ProspectTenantCard Id="ProspectTenant" {...commonProps} hasAnyTenants={hasAnyTenants} className={className} />}
    </div>
  );
};

interface TenantsProps {
  isEntity?: boolean;
  entityId?: number;
  className?: string;
  activeTab?: string;
  detailsActiveTab?: string;
}

const Tenants = ({ isEntity, entityId, className, detailsActiveTab }: TenantsProps) => {






  const { hasAccess, canWrite } = usePermissions();

  if (!hasAccess("Tenant", 1)) {
    return <NoAccess />;
  }

  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const initialSelectedOption = queryParams.get("selectedOption")
    ? Number(queryParams.get("selectedOption"))
    : TenantStatusesEnum.Current;

  const [fullyQualifiedFilter, setFullyQualifiedFilter] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(
    initialSelectedOption === TenantStatusesEnum.Current ? "Current" :
      initialSelectedOption === TenantStatusesEnum.Prospect ? "Prospect" :
        "Past"
  );

  const [filterKey, setFilterKey] = useState(queryParams.get("filterKey") || null);
  const [selectedFilterLabel, setSelectedFilterLabel] = useState(queryParams.get("filterLabel") || "All");

  const [tenantCounts, setTenantCounts] = useState({ Current: 0, Prospect: 0 });
  const [totalTenantCounts, setTotalTenantCounts] = useState({ Current: 0, Prospect: 0 });
  const [hasAnyTenants, setHasAnyTenants] = useState<boolean>(false);

  const [allAggregateValuesByTab, setAllAggregateValuesByTab] = useState<{
    Current: Record<string, number>;
    Prospect: Record<string, number>;
  }>({ Current: {}, Prospect: {} });

  const [aggregateValuesByTab, setAggregateValuesByTab] = useState<{
    Current: Record<string, number>;
    Prospect: Record<string, number>;
  }>({ Current: {}, Prospect: {} });

  // 1. Derive Selected Option safely
  const selectedOption = useMemo(() => {
    return activeTab === "Current" ? TenantStatusesEnum.Current :
      activeTab === "Prospect" ? TenantStatusesEnum.Prospect :
        TenantStatusesEnum.Past;
  }, [activeTab]);


  const getApiFunction = useCallback(
    () => async (
      organizationId: number,
      pageSize?: number,
      pageNumber?: number,
      skipValue?: number,
      filterEntityTypeId?: any,
      filterEntityIds?: Array<number>,
      fullyQualifiedFilters?: string,
      fullyQualifiedSorts?: string,
    ) => {
      if (isEntity && !entityId) {
        console.error("Error: entityId is required when isEntity is true");
        return;
      }

      const isProspect = selectedOption === TenantStatusesEnum.Prospect;

      // Wrap the primary fetch resolution to easily run it in parallel later
      const fetchPrimary = () => {
        if (isEntity) {
          return isProspect
            ? filterKey
              ? LeaseApplicationService.getApiLeaseApplicationPropertylist1(entityId!, filterKey, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, fullyQualifiedSorts)
              : LeaseApplicationService.getApiLeaseApplicationPropertylist(entityId!, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, fullyQualifiedSorts)
            : filterKey
              ? TenantService.getApiTenantPropertylist1(entityId!, filterKey, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, fullyQualifiedSorts)
              : TenantService.getApiTenantPropertylist(entityId!, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, fullyQualifiedSorts);
        } else {
          return isProspect
            ? filterKey
              ? LeaseApplicationService.getApiLeaseApplicationOrganizationlist1(organizationId, filterKey, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, fullyQualifiedSorts)
              : LeaseApplicationService.getApiLeaseApplicationOrganizationlist(organizationId, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, fullyQualifiedSorts)
            : filterKey
              ? TenantService.getApiTenantOrganizationlist1(organizationId, filterKey, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, fullyQualifiedSorts)
              : TenantService.getApiTenantOrganizationlist(organizationId, pageSize, pageNumber, skipValue, filterEntityTypeId, filterEntityIds, fullyQualifiedFilters, fullyQualifiedSorts);
        }
      };

      try {
        // 2. Fetch secondary tab ONLY when filterKey is null AND we are loading the first page
        if (!filterKey && pageNumber === 1) {
          const fetchSecondary = () => {
            if (isEntity) {
              return isProspect
                ? TenantService.getApiTenantPropertylist(entityId!, 10, 1, skipValue, filterEntityTypeId, filterEntityIds, undefined, undefined)
                : LeaseApplicationService.getApiLeaseApplicationPropertylist(entityId!, 10, 1, skipValue, filterEntityTypeId, filterEntityIds, undefined, undefined);
            } else {
              return isProspect
                ? TenantService.getApiTenantOrganizationlist(organizationId, 10, 1, skipValue, filterEntityTypeId, filterEntityIds, undefined, undefined)
                : LeaseApplicationService.getApiLeaseApplicationOrganizationlist(organizationId, 10, 1, skipValue, filterEntityTypeId, filterEntityIds, undefined, undefined);
            }
          };

          // 3. Parallel Execution for max performance
          const [primaryResponse, secondaryResponse] = await Promise.all([
            fetchPrimary(),
            fetchSecondary().catch(error => {
              console.error("Error fetching secondary tenant count:", error);
              return { recordCount: 0, aggregateValues: {} };
            })
          ]);

          const newCounts = {
            Current: isProspect ? secondaryResponse.recordCount || 0 : primaryResponse.recordCount || 0,
            Prospect: isProspect ? primaryResponse.recordCount || 0 : secondaryResponse.recordCount || 0,
          };

          const newAggregates = {
            Current: isProspect ? secondaryResponse.aggregateValues || {} : primaryResponse.aggregateValues || {},
            Prospect: isProspect ? primaryResponse.aggregateValues || {} : secondaryResponse.aggregateValues || {},
          };

          setTenantCounts(newCounts);
          setTotalTenantCounts(newCounts);
          setAllAggregateValuesByTab(newAggregates);
          setAggregateValuesByTab(newAggregates);

          return primaryResponse;
        } else {
          // When purely scrolling page 2+ or filtering, just await primary
          const primaryResponse = await fetchPrimary();

          setTenantCounts(prev => ({ ...prev, [activeTab]: primaryResponse.recordCount || 0 }));
          if (filterKey) {
            setAggregateValuesByTab(allAggregateValuesByTab);
          }

          return primaryResponse;
        }
      } catch (error) {
        console.error("Error fetching tenant data:", error);
        setTenantCounts(prev => ({ ...prev, [activeTab]: 0 }));
        return;
      }
    },
    [selectedOption, activeTab, isEntity, entityId, filterKey, allAggregateValuesByTab]
  );

  const {
    rows,
    loading,
    hasMore,
    rowCount,
    fetchData,
    currentSortDirection,
    handleSort,
    currentPage,
    aggregateValues,
  } = infiniteScrollHook({
    httpFetchDataFn: getApiFunction(),
    FullyQualifiedFilters: fullyQualifiedFilter,
    FullyQualifiedSorts: null, // Note: We pass standard sorting, but handle string dynamically below
    pageSize: 10,
  });

  // 4. Derive Sort instead of saving it to State
  const fullyQualifiedSort = useMemo(() => {
    if (currentSortDirection && currentSortDirection !== "none") {
      if (selectedOption === TenantStatusesEnum.Prospect) {
        return `Tenant.User.FirstName ${currentSortDirection}`;
      } else {
        return `User.FirstName ${currentSortDirection}`;
      }
    }
    return null;
  }, [currentSortDirection, selectedOption]);

  const handleSearchTermChange = useCallback((term: string) => {
    if (term) {
      const lowerTerm = term.toLowerCase();
      let filter = selectedOption === TenantStatusesEnum.Prospect
        ? `(Tenant.User.FirstName + " " + Tenant.User.LastName).ToLower().Contains("${lowerTerm}")`
        : `(User.FirstName + " " + User.LastName).ToLower().Contains("${lowerTerm}")`;

      setFullyQualifiedFilter(filter);
    } else {
      setFullyQualifiedFilter(null);
    }
  }, [selectedOption]);

  // 5. Batch specific state operations
  const handleTabChange = useCallback((tab: string) => {
    if (activeTab === tab) return;
    setActiveTab(tab);
    setFilterKey(null);
    setSelectedFilterLabel("All");
    setFullyQualifiedFilter(null);
  }, [activeTab]);

  const handleSelectChange = useCallback((value: string, label: string) => {
    if (value === "All") {
      setFilterKey(null);
      setSelectedFilterLabel("All");
    } else {
      setFilterKey(value);
      setSelectedFilterLabel(label);
    }
    setFullyQualifiedFilter(null);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const newFilterKey = params.get("filterKey");
    const newFilterLabel = params.get("filterLabel") || "All";

    if (newFilterKey !== filterKey || newFilterLabel !== selectedFilterLabel) {
      setFilterKey(newFilterKey);
      setSelectedFilterLabel(newFilterLabel);
    }
  }, [search]);

  // 6. ONE unified trigger for fetching data. Eliminates cascading renders.
  useEffect(() => {
    fetchData(1);
  }, [activeTab, filterKey, fullyQualifiedFilter, fullyQualifiedSort]);

  const filterOptions = useMemo(
    () => [
      { title: "Current", count: tenantCounts.Current },
      { title: "Prospect", count: tenantCounts.Prospect },
    ],
    [tenantCounts],
  );

  useEffect(() => {
    if (tenantCounts.Current > 0 || tenantCounts.Prospect > 0) {
      setHasAnyTenants(true);
    }
  }, [tenantCounts]);


  useEffect(() => {
    // Subscribe to the refresh topic
    const unsubscribe = PubSub.subscribe(
      TopicsEnum.RefreshLeaseData.toString(),
      () => {
        setFilterKey(null);
        setSelectedFilterLabel("All");
        fetchData(1); // Force-refresh the infinite scroll to page 1
      }
    );

    return () => unsubscribe();
  }, [fetchData]);

  const commonProps = {
    IsFooter: true,
    dataHookState: {
      rows,
      loading,
      hasMore,
      rowCount,
      fetchData,
      currentPage,
      aggregateValues,
    },
  };

  return (
    <Routes>
      <Route index
        element={
          <PermissionGuard permission="Tenant" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
            <MainContent
              className={className}
              handleSort={handleSort}
              currentSortDirection={currentSortDirection}
              rowCount={rowCount}
              handleSelectChange={handleSelectChange}
              onSearchTermChange={handleSearchTermChange}
              filterOptions={filterOptions}
              setActiveTab={handleTabChange} // Passed Batched Handler
              activeTab={activeTab}
              aggregateValuesByTab={aggregateValuesByTab}
              selectedFilterLabel={selectedFilterLabel}
              commonProps={commonProps}
              detailsActiveTab={detailsActiveTab}
              hasAnyTenants={hasAnyTenants}
              totalItemCount={totalTenantCounts[activeTab as keyof typeof totalTenantCounts] || 0}
              showAddButton={canWrite("Tenant")}
            />
          </PermissionGuard>
        }
      />
      <Route path="/create" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.All} fallback={<NoAccess />}>
          <AddTenant fetchData={fetchData} />
        </PermissionGuard>
      } />
      <Route path="/edit/:id" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.All} fallback={<NoAccess />}>
          <EditTenant fetchData={fetchData} />
        </PermissionGuard>
      } />
      <Route path="/currenttenantdetails/:id/:unitId" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
          <CurrentTenantDetails2 />
        </PermissionGuard>
      } />
      <Route path="/tenant-onboard/:id" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.All} fallback={<NoAccess />}>
          <LandlordTenantOnboard />
        </PermissionGuard>
      } />
      <Route path="/renew-lease/:id" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.All} fallback={<NoAccess />}>
          <RenewalLease />
        </PermissionGuard>
      } />
      <Route path="/move-out/:id" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.All} fallback={<NoAccess />}>
          <MoveInAndOutContentBlocks isMoveOut={true} />
        </PermissionGuard>
      } />
      <Route path="/review-application/:id" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
          <LeaseApplicationReviewView />
        </PermissionGuard>
      } />
      <Route path="/review-agreement/:id" element={
        <PermissionGuard permission="Tenant" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
          <LeaseAgreementReviewView />
        </PermissionGuard>
      } />
    </Routes>
  );
};

export default Tenants;